import React, { useEffect, useRef } from 'react';
import { HeatDto, TeamDto, startHeat } from '../../../api/apis';

interface HeatDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  heat: HeatDto;
  teams: TeamDto[];
  isLoading: boolean;
  canStart: boolean;
  onHeatStarted: () => void;
}

const HeatDetailsModal: React.FC<HeatDetailsModalProps> = ({
  isOpen,
  onClose,
  heat,
  teams,
  isLoading,
  canStart,
  onHeatStarted,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Prevent scrolling on the body when modal is open
      document.body.style.overflow = 'hidden';
      
      // Focus the modal when it opens
      modalRef.current?.focus();
    } else {
      // Restore scrolling when modal is closed
      document.body.style.overflow = 'unset';
    }

    return () => {
      // Cleanup: restore scrolling when component unmounts
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleStartHeat = async () => {
    try {
      await startHeat(heat.id);
      onHeatStarted(); // Call the refresh function
      onClose();
    } catch (error) {
      console.error('Failed to start heat:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        // Close modal when clicking the backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        ref={modalRef}
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        tabIndex={-1} // Make the modal focusable
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 id="modal-title" className="text-2xl font-semibold">{heat.name}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {isLoading ? (
            <div className="text-center py-4">Loading teams...</div>
          ) : (
            <>
              <div className="space-y-2">
                {teams.map((team) => (
                  <div key={team.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center space-x-4 flex-1 min-w-0">
                      <span className="font-medium truncate">{team.name}</span>
                      <span className="text-gray-500">|</span>
                      <span className="text-gray-600 truncate">{team.category}</span>
                      <span className="text-gray-500">|</span>
                      <span className="text-gray-600 truncate">
                        {team.members.join(', ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {canStart && (
                <div className="mt-6">
                  <button
                    onClick={handleStartHeat}
                    className="w-full bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Start Heat
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeatDetailsModal; 